import type { PsHttpSSE } from '@patsnap-utils/sse';
import { FactoryPsHttpSSE } from '@patsnap-utils/sse';
import { PsHttpService, type PatSnapCommonResponseData } from '@patsnap/tahiti-core/common/ps-http';
import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';

export interface VisualReportEventData {
    content: string;
    status: string;
}

export type VisualReportTriggerStatus = 'cached' | 'started' | 'in_progress';

export interface VisualReportApi {
    getVisualReportStream(params: { text: string; isLogin: boolean; refresh_cache?: boolean }): PsHttpSSE<VisualReportEventData>;
    checkVisualReportCache(params: { text: string; isLogin: boolean }): Promise<{ cached: boolean }>;
    triggerVisualReport(params: { text: string; isLogin: boolean; refresh_cache?: boolean }): Promise<{ status: VisualReportTriggerStatus }>;
}

export const VisualReportApi = Symbol('VisualReportApi');

@injectable()
export class VisualReportApiImpl extends PsHttpService implements VisualReportApi {
    get options() {
        return {
            baseURL: `${import.meta.env.VITE_SYNAPSE_URI}/api/ai-service`,
            baseURLGuest: `${import.meta.env.VITE_SYNAPSE_URI}/api/ai-service/guest`,
        };
    }

    @inject(FactoryPsHttpSSE)
    private _factorySsePsHttp!: FactoryPsHttpSSE;

    private getBaseURL(isLogin: boolean): string {
        return isLogin ? this.options.baseURL : this.options.baseURLGuest;
    }

    getVisualReportStream({ text, isLogin, refresh_cache }: { text: string; isLogin: boolean; refresh_cache?: boolean }) {
        return this._factorySsePsHttp<VisualReportEventData>()
            .init({
                url: `${this.getBaseURL(isLogin)}/pia/visual_report/stream`,
                data: { text, refresh_cache },
                hasFinalMessage: () => true,
            });
    }

    checkVisualReportCache({ text, isLogin }: { text: string; isLogin: boolean }): Promise<{ cached: boolean }> {
        return this.post<PatSnapCommonResponseData<{ cached: boolean }>>({
            url: `${this.getBaseURL(isLogin)}/pia/visual_report/cache/check`,
            data: { text },
        }).then(res => {
            return res as unknown as { cached: boolean }
        });
    }

    triggerVisualReport({ text, isLogin, refresh_cache }: { text: string; isLogin: boolean; refresh_cache?: boolean }): Promise<{ status: VisualReportTriggerStatus }> {
        return this.post<PatSnapCommonResponseData<{ status: VisualReportTriggerStatus }>>({
            url: `${this.getBaseURL(isLogin)}/pia/visual_report/trigger`,
            data: { text, refresh_cache },
        }).then(res => res as unknown as { status: VisualReportTriggerStatus });
    }
}
